import React, { useState, useRef, useLayoutEffect } from "react";

interface ExpandableInstructionsProps {
  instructionsText: string;
  lineLimit?: number;
  textColor?: string;
  textStyle?: React.CSSProperties;
}

const ExpandableInstructions: React.FC<ExpandableInstructionsProps> = ({
  instructionsText,
  lineLimit = 1, // Default to 1 line for consistent behavior
  textColor = "text-white",
  textStyle,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!measureRef.current || !instructionsText) {
      setNeedsTruncation(false);
      return;
    }

    // Small delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      if (!measureRef.current) return;

      try {
        // Get the line height and calculate expected height for the line limit
        const computedStyle = window.getComputedStyle(measureRef.current);
        let lineHeight = 20; // fallback line height

        if (computedStyle && computedStyle.lineHeight) {
          const lineHeightValue = computedStyle.lineHeight;
          if (lineHeightValue !== "normal") {
            lineHeight = parseFloat(lineHeightValue);
          } else if (computedStyle.fontSize) {
            lineHeight = parseFloat(computedStyle.fontSize) * 1.2;
          }
        }

        const expectedHeight = lineHeight * lineLimit;
        const actualHeight = measureRef.current.scrollHeight;

        // If actual height is significantly more than expected height for the line limit, truncation is needed
        setNeedsTruncation(actualHeight > expectedHeight + 2); // +2px tolerance for sub-pixel rendering
      } catch (error) {
        // Fallback: check if text is likely too long (simple heuristic for tests)
        const textLength = instructionsText.length;
        const estimatedCharsPerLine = 50; // rough estimate
        const estimatedLines = Math.ceil(textLength / estimatedCharsPerLine);
        setNeedsTruncation(estimatedLines > lineLimit);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [instructionsText, lineLimit]);

  if (!instructionsText) {
    return <span style={textStyle}>No Instructions</span>;
  }

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleCollapse();
    }
  };

  const buttonClassName = textStyle
    ? "hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 rounded inline transition-opacity duration-150"
    : `${textColor} hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 rounded inline transition-colors duration-150`;

  const buttonStyle = textStyle ? { ...textStyle } : undefined;

  // Base styles for consistent text rendering
  const baseTextStyle = {
    ...textStyle,
    wordBreak: "break-word" as const,
    overflowWrap: "break-word" as const,
  };

  const truncatedContainerStyle = {
    position: "relative" as const,
    ...baseTextStyle,
  };

  const truncatedTextStyle = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical" as const,
    WebkitLineClamp: lineLimit,
    overflow: "hidden",
    ...baseTextStyle,
  };

  const expandButtonOverlayStyle = {
    position: "absolute" as const,
    right: "0",
    bottom: "0",
    background: "inherit", // Match parent background
    paddingLeft: "4px", // Small overlap to cover potential text
    lineHeight: "1", // Ensure button aligns with text line height
    ...buttonStyle,
  };

  return (
    <>
      {/* Hidden element to measure full text height */}
      <span
        ref={measureRef}
        style={{
          ...baseTextStyle,
          position: "absolute",
          visibility: "hidden",
          height: "auto",
          width: textRef.current?.parentElement?.offsetWidth || 300, // Use parent width or fallback
          top: "-9999px",
          left: "-9999px",
        }}
      >
        {instructionsText}
      </span>

      {/* Visible text */}
      {isCollapsed ? (
        <span style={truncatedContainerStyle}>
          <span ref={textRef} style={truncatedTextStyle}>
            {instructionsText}
          </span>

          {needsTruncation && (
            <button
              onClick={toggleCollapse}
              onKeyDown={handleKeyDown}
              className={buttonClassName}
              style={expandButtonOverlayStyle}
              aria-label="Expand instructions"
              tabIndex={0}
            >
              ▼
            </button>
          )}
        </span>
      ) : (
        <span style={baseTextStyle}>
          {instructionsText}
          {needsTruncation && (
            <>
              {" "}
              <button
                onClick={toggleCollapse}
                onKeyDown={handleKeyDown}
                className={buttonClassName}
                style={buttonStyle}
                aria-label="Collapse instructions"
                tabIndex={0}
              >
                ▲
              </button>
            </>
          )}
        </span>
      )}
    </>
  );
};

export default ExpandableInstructions;
